import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import { useLocale } from '../i18n/LocaleProvider';
import { downloadFile } from '../ui/download';
import { Alert, Field, Loading, useAction } from '../ui/ui';

export function MaintenanceDocuments({
  maintenanceId,
  session,
}: {
  readonly maintenanceId: string;
  readonly session: CurrentSession;
}) {
  const client = useApiClient();
  const queries = useQueryClient();
  const action = useAction();
  const ar = useLocale().locale === 'ar';
  const canView = [
    'maintenance.create_edit',
    'maintenance.planning',
    'maintenance.close',
    'maintenance.cancel_planned',
  ].some((key) => session.permissions.includes(key));
  const canUpload = session.permissions.includes('maintenance.create_edit');
  const documents = useQuery({
    queryKey: ['maintenance-documents', maintenanceId],
    enabled: canView,
    queryFn: async ({ signal }) => {
      const response = await client.listMaintenanceDocuments({ path: { maintenanceId } }, { signal });
      if (response.status !== 200) throw new Error('maintenance_documents_unavailable');
      return response.body.documents;
    },
  });
  if (!canView && !canUpload) return null;
  return (
    <section className="er-card">
      <h2>{ar ? 'مرفقات الصيانة' : 'Maintenance attachments'}</h2>
      {action.error && <Alert kind="error">{action.error}</Alert>}
      {canUpload && (
        <Field id="maintenance-document-file" label={ar ? 'إرفاق مستند للصيانة' : 'Attach maintenance document'}>
          <input
            id="maintenance-document-file"
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            disabled={action.busy}
            onChange={(event) => {
              const input = event.target;
              const file = input.files?.[0];
              if (!file) return;
              if (file.type !== 'application/pdf' && file.type !== 'image/jpeg' && file.type !== 'image/png') {
                action.setError(ar ? 'اختر ملف PDF أو JPEG أو PNG.' : 'Choose a PDF, JPEG or PNG file.');
                return;
              }
              const contentType = file.type;
              void action.run(async (requestKey) => {
                const response = await client.uploadMaintenanceDocument({
                  path: { maintenanceId },
                  headers: { 'idempotency-key': requestKey },
                  body: new Uint8Array(await file.arrayBuffer()),
                  contentType,
                });
                if (response.status === 200 || response.status === 201) {
                  await queries.cancelQueries({ queryKey: ['maintenance-documents', maintenanceId] });
                  await queries.invalidateQueries({ queryKey: ['maintenance-documents', maintenanceId] });
                  input.value = '';
                }
                return response;
              });
            }}
          />
        </Field>
      )}
      {canView && documents.isPending && <Loading />}
      {canView && documents.isError && (
        <Alert kind="error">{ar ? 'تعذر تحميل مرفقات الصيانة.' : 'Maintenance attachments could not be loaded.'}</Alert>
      )}
      {documents.data && documents.data.length === 0 && (
        <p>{ar ? 'لا توجد مرفقات للصيانة.' : 'No maintenance attachments.'}</p>
      )}
      {documents.data && documents.data.length > 0 && (
        <ul>
          {documents.data.map((document) => (
            <li key={document.versionId}>
              {document.createdAt} · {document.contentType} · {document.byteSize} B
              <button
                className="er-button"
                type="button"
                disabled={action.busy}
                onClick={() => {
                  void action.run(async () => {
                    const response = await client.downloadMaintenanceDocument({
                      path: { maintenanceId, versionId: document.versionId },
                    });
                    if (response.status === 200)
                      downloadFile(
                        response.body,
                        `maintenance-document-v${document.versionNumber}.${document.contentType === 'application/pdf' ? 'pdf' : document.contentType === 'image/png' ? 'png' : 'jpg'}`,
                      );
                    return response;
                  });
                }}
              >
                {ar ? 'تنزيل المستند' : 'Download document'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
